import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Printer } from "lucide-react";

const Section = ({ title, children }) => (
  <div className="border border-[#27272A] bg-[#101013] p-4" data-testid={`brief-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">{title}</div>
    {children}
  </div>
);
const List = ({ items, empty = "None recorded" }) =>
  items && items.length ? (
    <ul className="list-disc pl-5 space-y-1 text-sm text-[#E4E4E7]">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
  ) : <div className="text-sm text-[#71717A]">{empty}</div>;

export default function ConceptBrief() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const productId = params.get("product") || "cp:portable_blender";
  const [mode, setMode] = useState(params.get("mode") || "current");
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setB(null); setErr("");
    api.get("/capital-review/brief", { params: { product_id: productId, mode } })
      .then((r) => setB(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load brief"));
  }, [productId, mode]);

  return (
    <div className="p-8 max-w-[880px]" data-testid="concept-brief-page">
      <button onClick={() => nav(`/capital-review?product=${productId}`)} className="text-[11px] font-mono text-[#71717A] hover:text-[#A1A1AA] flex items-center gap-1 mb-3" data-testid="brief-back">
        <ArrowLeft size={12} /> Back to Capital Review
      </button>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-head text-3xl font-bold">{b?.title || "Opportunity Brief"}</h1>
        <button onClick={() => window.print()} className="text-[11px] font-mono text-[#A1A1AA] border border-[#27272A] px-3 py-1.5 flex items-center gap-1 hover:bg-[#18181b]" data-testid="brief-print"><Printer size={12} /> Print</button>
      </div>

      <div className="flex gap-2 mt-3" data-testid="brief-mode-toggle">
        {["current", "decision"].map((m) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`brief-mode-${m}`}
            className={`text-[11px] font-mono px-3 py-1 border ${mode === m ? "text-sky-200 border-sky-400/40 bg-sky-500/10" : "text-[#71717A] border-[#27272A]"}`}>
            {m === "current" ? "Current research" : "Decision-time snapshot"}
          </button>
        ))}
      </div>

      {err && <div className="border border-amber-400/30 bg-amber-500/[0.05] text-amber-200 text-sm p-3 mt-4" data-testid="brief-error">{err}</div>}
      {!b && !err && <div className="text-[#71717A] font-mono text-sm mt-6">Loading brief…</div>}

      {b && (
        <div className="mt-4 space-y-3">
          <div className="text-[11px] font-mono text-[#52525B]">
            {b.mode === "DECISION_SNAPSHOT" ? `As of decision made ${String(b.as_of).slice(0, 16)}` : "As of latest evidence"}
          </div>

          {b.mode === "CURRENT_RESEARCH" ? (
            <>
              <Section title="Why we are watching it"><List items={b.why_watching} /></Section>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Section title="What customers dislike"><List items={b.customer_dislikes} empty="No recurring complaints found" /></Section>
                <Section title="What customers already love"><List items={b.customer_loves} empty="Not enough praise evidence" /></Section>
              </div>
              <Section title="Version being considered">
                {b.version_considered ? (
                  <div className="text-sm text-[#E4E4E7]"><div className="font-medium">{b.version_considered.name}</div>
                    <List items={b.version_considered.improvements} /></div>
                ) : <div className="text-sm text-[#71717A]">No specific version proposed yet</div>}
              </Section>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Section title="Can suppliers make it?">
                  <div className="text-sm text-[#E4E4E7]">{b.suppliers.status} · {b.suppliers.suppliers_observed} supplier(s) observed</div>
                </Section>
                <Section title="Current economics">
                  <div className="text-sm text-[#E4E4E7]">Test price: {b.economics.selling_price == null ? "—" : `$${b.economics.selling_price}`}</div>
                  <div className="text-sm text-[#E4E4E7]">Money before ads: {b.economics.money_before_ads == null ? "—" : `~$${b.economics.money_before_ads}/order`}</div>
                  <div className="text-[10px] text-[#71717A] font-mono mt-1">{b.economics.note}</div>
                </Section>
              </div>
              <Section title="How much cash could be at risk">
                <div className="text-sm text-[#E4E4E7]">Smallest known upfront: {b.cash_at_risk.smallest_known_upfront == null ? "—" : `~$${b.cash_at_risk.smallest_known_upfront}`}</div>
                <div className="text-[10px] text-[#71717A] font-mono mt-1">{b.cash_at_risk.note}</div>
              </Section>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Section title="What could go wrong"><List items={b.what_could_go_wrong} empty="No blocking/important risks recorded" /></Section>
                <Section title="What we still don't know"><List items={b.still_unknown} empty="No major unknowns recorded" /></Section>
              </div>
              <Section title="Are we ready to make a money decision?">
                <div className="text-sm text-[#E4E4E7]">{b.readiness.plain || b.readiness.state}</div>
                <div className="text-[10px] text-[#71717A] font-mono mt-1">Evidence: {b.evidence_quality}</div>
              </Section>
            </>
          ) : (
            <>
              <Section title="Analyst decision">
                <div className="text-lg text-[#E4E4E7]">{b.analyst_decision}</div>
                <div className="text-[10px] text-[#71717A] font-mono">by {b.decided_by || "—"}</div>
                {b.reasoning && <div className="text-sm text-[#A1A1AA] mt-2">"{b.reasoning}"</div>}
                {b.what_would_change_my_mind && <div className="text-[11px] text-[#71717A] mt-1">Would change mind: {b.what_would_change_my_mind}</div>}
              </Section>
              <Section title="What was known when the decision was made">
                <div className="text-sm text-[#E4E4E7] space-y-1">
                  <div>Readiness: {b.known_at_decision.readiness_state}</div>
                  <div>Evidence quality: {b.known_at_decision.evidence_quality}</div>
                  <div>Risk: {b.known_at_decision.risk_headline}</div>
                  <div>Money before ads: {b.known_at_decision.money_before_ads == null ? "—" : `~$${b.known_at_decision.money_before_ads}/order`}</div>
                </div>
              </Section>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Section title="Unresolved gaps then"><List items={b.known_at_decision.unresolved_gaps} /></Section>
                <Section title="Biggest unknowns then"><List items={b.known_at_decision.biggest_unknowns} /></Section>
              </div>
            </>
          )}

          <div className="text-[10px] text-[#52525B] font-mono border-t border-[#1c1c1f] pt-3">{b.disclaimer}</div>
        </div>
      )}
    </div>
  );
}
